import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import { getColorByAuditStatus } from "./getColorByAuditStatus";
import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import { http } from "src/axios";
import { useMutation } from "@tanstack/react-query";
import CheckIcon from "@mui/icons-material/Check";
import { useContext, type FC } from "react";
import { AuditRequestStatusNumber, type AuditRequest } from "src/types/audits";
import { AuditContext } from "../AuditContext";
import toast from "react-hot-toast";

const markAuditRequestAsAccepted = ({ requestId }: AuditRequest) =>
  http.put(`/api/v2/vendors/audit`, { request_id: requestId, status: AuditRequestStatusNumber.Queued });

const AuditMarkInviteAsAcceptedButton: FC<{ auditRequest: AuditRequest }> = ({ auditRequest }) => {
  const { refetchAudits } = useContext(AuditContext);
  const mutation = useMutation({
    mutationKey: ["audit-request-mark-invite-as-accepted", auditRequest.requestId],
    mutationFn: markAuditRequestAsAccepted,
    onSuccess: () => {
      toast.success("Invite marked as accepted");
      refetchAudits();
    },
  });

  const onClick = () => mutation.mutate(auditRequest);

  return (
    <Button onClick={onClick} disabled={mutation.isPending || mutation.isSuccess} variant="outlined">
      {mutation.isPending ? (
        <CircularProgress size={12} />
      ) : mutation.isSuccess ? (
        <CheckIcon />
      ) : (
        /* show normal icon on error, user is already notified with a toast */
        <CheckIcon />
      )}
      <span>{mutation.isSuccess ? `Invite marked as accepted` : `Mark invite as accepted`}</span>
    </Button>
  );
};

export const AuditActionsCell: FC<GridCellParams<AuditRequest>> = ({ row }) => {
  if (row.status !== "waiting for invite acceptance") return null;

  return <AuditMarkInviteAsAcceptedButton auditRequest={row} />;
};

const columns: GridColDef[] = [
  {
    field: "requestId",
    headerName: "Request ID",
    width: 150,
  },
  {
    field: "store",
    headerName: "Store",
    width: 150,
  },
  {
    field: "market",
    headerName: "Market",
    width: 150,
  },
  {
    field: "account",
    headerName: "Account",
    width: 150,
  },
  {
    field: "status",
    headerName: "Status",
    width: 240,
    renderCell: (params: any) => {
      const color = getColorByAuditStatus(params.value);
      return (
        <Box display="flex" alignItems="center">
          <Box width="12px" minWidth="12px" height="12px" bgcolor={color} borderRadius="2px" />
          <Box ml={1} sx={{ textTransform: "capitalize" }}>
            {params.value}
          </Box>
        </Box>
      );
    },
  },
  {
    field: "_actions",
    headerName: "Actions",
    width: 220,
    display: "flex",
    renderCell: AuditActionsCell,
  },
  {
    field: "requestedDate",
    headerName: "Requested Date",
    width: 250,
  },
  {
    field: "completedDate",
    headerName: "Completed Date",
    width: 250,
  },
  {
    field: "chargebackMonths",
    headerName: "Chargeback Months",
    type: "number",
    align: "left",
    headerAlign: "left",
    width: 150,
  },
  {
    field: "shortageMonths",
    headerName: "Shortage Months",
    type: "number",
    align: "left",
    headerAlign: "left",
    width: 150,
  },
  {
    field: "accuralMonths",
    headerName: "Accural Months",
    type: "number",
    align: "left",
    headerAlign: "left",
    width: 150,
  },
  {
    field: "poMonths",
    headerName: "PO Months",
    type: "number",
    align: "left",
    headerAlign: "left",
    width: 150,
  },
  {
    field: "errorMessage",
    headerName: "Error Message",
    align: "left",
    headerAlign: "left",
    width: 250,
    renderCell: ({ value }) => (
      <Tooltip title={value}>
        <span>{value}</span>
      </Tooltip>
    ),
  },
];

export const getAuditsGridColumns = (): GridColDef[] => columns;
